/* eslint-disable no-unused-vars */
import * as d3 from 'd3';
import _ from 'lodash';
import * as topojson from 'topojson-client';
import createBase from '../utils/d3-base';
import { intcomma } from 'journalize';
import {
  highways,
  cities,
  add_legend_for_gray,
  rate_perResidents,
  relocateTooltip,
} from '../utils/map-utils';
import * as ss from 'simple-statistics';
import loadAssetURL from '../utils/load-asset-url';

const partners = window.location.search;
const isPartners = partners ? true : false;
const TT_logo_present = loadAssetURL('TT-logo-present');

import topology from 'Assets/geography/texas_counties_roads.json';
import topology_hsa from 'Assets/geography/texas_tsas.json';
import data from '../../../data/map/tx_hospitals_map.json';

const container = d3.select('#tx-hospitals-map');

const getFrameWidth = () => container.node().offsetWidth;

// a helper function to clear the container of its contents
const clearContainer = () => container.html('');

// breakpoint for mobile
let breakPoint = 544;

// Toggle buttons

// let btnContainer = document.querySelector('#btnGroup');
// let btns = btnContainer.getElementsByClassName('button');

// for (var t = 0; t < btns.length; t++) {
//   btns[t].addEventListener('click', function (event) {
//     var currentParent = document.querySelector('#btnGroup');
//     var current = currentParent.getElementsByClassName('active');
//     current[0].className = current[0].className.replace(' active', '');
//     this.className += ' active';
//     defVal = this.value;
//     let promises = [topology, defVal];
//     Promise.all(promises).then(ready);
//   });
// }

// sets geo stuff for projection
const geoPath = d3.geoPath();

const geoIdentity = d3.geoIdentity().reflectY(true);

// establish color bucket breakdown
let color = d3.scaleQuantize().range([
  // '#ffffe0',
  // '#FFF5B2',
  '#FDDE85',
  // '#FDBB45', // take out some of the middle colors to accentuate extremes
  '#FDA23E',
  // '#F2691D',
  '#DB3F2A',
  // '#A52C20',
  '#6C1D1C',
]);

const latest_date = data['latest_date'];

const cases = data[latest_date]['cases'];
const cases_prev = data[latest_date]['cases_prev'];

const popData = d3.map(),
  totalBedsData = d3.map(),
  bedsAvailData = d3.map(),
  icuBedsData = d3.map(),
  ventsAvailData = d3.map(),
  covidPatientsData = d3.map(),
  rateData = d3.map(),
  staffedInpatientBeds = d3.map();

// sets the data for cases, rate per 1000 and total_beds
cases.forEach(d =>
  rateData.set(
    d.trauma_service_area,
    rate_perResidents(+d.covid_patients, +d.total_beds, 100)
  )
);
cases.forEach(d => popData.set(d.trauma_service_area, +d['2020_pop']));
cases.forEach(d => totalBedsData.set(d.trauma_service_area, +d.total_beds));
cases.forEach(d => bedsAvailData.set(d.trauma_service_area, +d.beds_avail));
cases.forEach(d => icuBedsData.set(d.trauma_service_area, +d.icu_beds_avail));
cases.forEach(d => ventsAvailData.set(d.trauma_service_area, +d.vent_avail));
cases.forEach(d =>
  covidPatientsData.set(d.trauma_service_area, +d.covid_patients)
);
cases.forEach(d =>
  staffedInpatientBeds.set(d.trauma_service_area, +d.staffed_inpatient_beds)
);

// Make object into array so we can find the domain of data for color scale
let dataArray = Object.keys(rateData).map(key => rateData[key]);

export default function renderHospitalMap() {
  clearContainer();

  const frameWidth = getFrameWidth(); // calcuates the width on each render

  // Establishes as isMobile function

  const isMobile = frameWidth < breakPoint;

  // uses `./utils/d3-base` to set up a bunch of conventional d3 stuff

  const map = createBase({
    container, // the chart container
    height: isMobile ? frameWidth + (isPartners ? 120 : 70) : 600, // mobile height
  });

  // Creates the svg for the map

  const svg = map.g.append('g').attr('class', 'g_rate_map');

  let promises = [topology, topology_hsa];

  Promise.all(promises).then(ready);

  function ready([shapefiles]) {
    // Creates title and map base for the rate map

    const state = topojson.merge(
        shapefiles,
        shapefiles.objects.counties.geometries
      ),
      // counties = topojson.feature(shapefiles, shapefiles.objects.counties),
      texas_highways = topojson.feature(shapefiles, shapefiles.objects.roads),
      major_cities = topojson.feature(shapefiles, shapefiles.objects.places),
      hsas = topojson.feature(topology_hsa, topology_hsa.objects.tsa);

    // Finding ckmeans Thresholds for scale, an alternative to the usual scale

    // More info here: https://observablehq.com/@d3/quantile-quantize-and-threshold-scales

    // https://medium.com/@dschnr/using-clustering-to-create-a-new-d3-js-color-scale-dec4ccd639d2

    const numberOfClasses = color.range().length - 1;

    let x_legend = d3
      .scaleLinear()
      .rangeRound([
        map.width * (isMobile ? 0.1 : 0.58),
        map.width * (isMobile ? 0.9 : 0.825),
      ]);

    // use this domain just for the hospitals map color scale
    let startRange = 0,
      endRange = 15,
      newDomain = d3.range(startRange, endRange, 5);
    newDomain.push(endRange); // push the max number in, not included in d3.range()'s array
    
    // Establish the domain
    color.domain(newDomain);
    x_legend.domain(newDomain);

    const projection = geoIdentity.fitExtent(
      [
        [0, isMobile ? 75 : 0],
        [map.width, map.height],
      ],
      state
    );

    const path = geoPath.projection(projection);

    // Adding Texas county shapefile
    // const counties_shape = svg
    //   .selectAll('g')
    //   .data(counties.features)
    //   .enter()
    //   .append('path')
    //   .attr('class', 'counties')
    //   .attr('fill', '#e8e8e8')
    //   .attr('stroke', 'rgba(255, 255, 255, .6)')
    //   .attr('d', path);

    // Adding Texas HSA shapefile
    const hsas_shape = svg
      .selectAll('g')
      .data(hsas.features)
      .enter()
      .append('path')
      .attr('class', 'hsas')
      .attr('fill', d => {
        let hospital_beds_in_use = rateData.get(d.properties.TSA);
        if (hospital_beds_in_use) {
          var domain_reverse = newDomain.slice().reverse();

          for (var i = 0; i < domain_reverse.length; i++) {
            if (hospital_beds_in_use > domain_reverse[i]) {
              return color.range()[numberOfClasses - i];
            }
          }
        } else {
          return '#e8e8e8';
        }
      })
      .attr('stroke', 'rgba(255, 255, 255, .6)')
      .attr('d', path);

    // Adds highways (the order of this is very important)
    highways(svg, texas_highways, path);

    // Add cities
    cities(svg, major_cities, path);

    const tooltip = container
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0);

    hsas_shape.on('mouseover', MouseOver).on('mouseout', MouseOut);

    function MouseOver(d, i) {
      const active_county = svg
        .append('path')
        .datum(d)
        .attr('d', path)
        .classed('active_county', true);

      const pop = popData.get(d.properties.TSA),
        total_beds = totalBedsData.get(d.properties.TSA),
        beds_avail = bedsAvailData.get(d.properties.TSA),
        icu_beds = icuBedsData.get(d.properties.TSA),
        vents_avail = ventsAvailData.get(d.properties.TSA),
        covid_patients = covidPatientsData.get(d.properties.TSA),
        rate = rateData.get(d.properties.TSA),
        staffed_inpatient_beds = staffedInpatientBeds.get(d.properties.TSA);

      var tooltip_div = `<div class='county-title'>${
        d.properties.RAC
      } </div><div style="margin-bottom: 0" class='county-data'>Population: ${
        pop ? intcomma(pop) : 0
      }</div><div style="margin: 0.312rem 0;" class='county-data'>COVID-19 patients take up <span style='font-weight:bold;'>${
        rate ? rate.toFixed(1) : 0
      }%</span> of the region's <br /> hospital capacity.</div><table class="dv-table"><tbody><tr><td class='tooltip_header'>
          COVID-19 patients
        </td><td class="number text-right">
        ${covid_patients ? intcomma(covid_patients) : 0}
        </td></tr><tr><td class='tooltip_header'>
          Total beds
        </td><td class="number text-right">
        ${total_beds ? intcomma(total_beds) : 0}
        </td></tr><tr><td class='tooltip_header'>
         ICU beds available
        </td><td class="number text-right">
          ${icu_beds ? intcomma(icu_beds) : 0}
        </td></tr></tbody></table>`;

      tooltip.style('opacity', 0.9).html(tooltip_div);

      relocateTooltip(
        tooltip,
        d3.event.pageX,
        d3.event.pageY,
        d3.event.screenY
      );
    }

    function MouseOut(d) {
      // Add interactivity
      tooltip.style('opacity', 0);
      d3.selectAll('.active_county').remove();
    }

    const legend = svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${isMobile ? 0 : map.width / -50},${15})`);

    const rate_legend = legend
      .append('g')
      .attr('class', 'rate_legend')
      .attr('transform', `translate(${isMobile ? 0 : 0},${0})`);

    // Creates legend based on rate data

    const legend_rect_width =
      (x_legend.range()[1] - x_legend.range()[0]) / numberOfClasses;
    
    const legendText = rate_legend
      .append('g')
      .attr('class', 'tick')
    
    if (isMobile) {
      legendText
        .append('g')
        .attr('transform', `translate(${x_legend.range()[0]},-8)`)
        .append('text')
        .text('Hospital beds used by COVID-19 patients')
    } else {
      legendText
        .append('g')
        .attr('transform', `translate(${x_legend.range()[0]},-23)`)
        .append('text')
        .text('Percent of hospital beds occupied')

      legendText
        .append('g')
        .attr('transform', `translate(${x_legend.range()[0]},-8)`)
        .append('text')
        .text('by COVID-19 patients')
    }
      
    rate_legend
      .selectAll('rect')
      .data(
        color.range().map(function(d) {
          d = color.invertExtent(d);
          if (d[0] == null) d[0] = -1;
          return d;
        })
      )
      .enter()
      .append('rect')
      .attr('height', 10)
      .attr('x', (d, i) => x_legend.range()[0] + i * legend_rect_width)
      .attr('width', (d, i) => legend_rect_width + 2)
      .attr('fill', d => color(d[0]));

    // manually add ticks to the legend!
    let ticks = rate_legend.append('g').attr('class', 'ticks');

    let tickOne = ticks
      .append('g')
      .attr(
        'transform',
        `translate(${x_legend.range()[0]},0)`
      )
      .attr('class', 'tick');

    tickOne
      .append('line')
      .attr('stroke', 'black')
      .attr('stroke-width', '0.75px')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', 15);

    tickOne
      .append('text')
      .text(newDomain[0].toFixed(0) + '%')
      .attr('x', 0)
      .attr('y', 27)
      .attr('text-anchor', 'middle');

    let tickTwo = ticks
      .append('g')
      .attr(
        'transform',
        `translate(${x_legend.range()[0] + legend_rect_width * 3},0)`
      )
      .attr('class', 'tick');

    tickTwo
      .append('line')
      .attr('stroke', 'black')
      .attr('stroke-width', '0.75px')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', 15);

    tickTwo
      .append('text')
      .text(newDomain[newDomain.length - 1].toFixed(0) + '%+')
      .attr('x', 0)
      .attr('y', 27)
      .attr('text-anchor', 'middle');

    // rate_legend
    //   .call(
    //     d3
    //       .axisBottom(x_legend)
    //       .tickSize(15)
    //       .tickFormat(
    //         (d, i) =>
    //           i ? newDomain[newDomain.length - 1].toFixed(0) + '%+' : '40%' // HACK
    //       )
    //       .tickValues(color.domain())
    //   )
    //   .select('.domain')
    //   .remove();

    // This adds the "No reported cases" gray box below the legend
    // add_legend_for_gray(legend, 'rate', x_legend, isMobile);
  }

  const partners_img_width = map.width * (isMobile ? 1 : 0.5);
  const partners_img_height = partners_img_width / 13.3;

  if (isPartners) {
    const partner_link = svg
      .append('a')
      .attr('xlink:href', 'https://www.texastribune.org/')
      .attr('target', '_parent')
      .attr('rel', 'noopener noreferrer');

    partner_link
      .append('svg:image')
      .attr('alt', 'Coronavirus tracker presented by The Texas Tribune')
      .attr('x', -20)
      .attr('y', map.height - partners_img_height / 2)
      .attr(
        'width',
        frameWidth > 800 ? partners_img_width * 0.7 : partners_img_width
      )
      .attr('height', partners_img_height)
      .attr('xlink:href', TT_logo_present);
  }
}

// function resize() {
//   // code executed on window resize
//   let frameWidth = getFrameWidth(),
//     frameHeight;

//   if (frameWidth < breakPoint) {
//     frameHeight = 475;
//   } else {
//     frameHeight = 600;
//   }

//   let chartWidth = frameWidth,
//     chartHeight = frameHeight;
//   let promises = [topology];

// }

// // call resize on load
// resize();
// window.addEventListener('resize', _.debounce(resize, 200));
